import { DealLinkTile } from '@dealstack/frontend/app/pages/Project/DealDashboard/common/DealLinkTile.tsx';
import { DealMipCard } from '@dealstack/frontend/app/pages/Project/DealDetails/DealMipCard.tsx';
import {
	MipSectionWrapper,
	StyledCreateMipDropdown,
	StyledCreateMipItem,
} from '@dealstack/frontend/app/pages/Project/DealDetails/styled.ts';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { AssetsLoader } from '@dealstack/frontend/shared/components/AssetsLoader/AssetsLoader.tsx';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader.tsx';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { SummaryDealMip } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Button, ButtonProps, Flex, Grid, Menu, Skeleton } from '@mantine/core';
import { generatePath } from 'react-router-dom';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

type LinkTile = {
	title: string;
	link: string;
	icon: PredefinedIcons;
	imageBg?: string;
	dataTestId?: string;
};

interface Props {
	createMipLoading: boolean;
	createNewMip: () => void;
	openQuickCreate: () => void;
	onIPAccess: (mip: SummaryDealMip) => void;
	dealId: string;
	mips: SummaryDealMip[];
	isRefreshingPermissions: boolean;
}

export const DealDetailsContent = ({
	createMipLoading,
	createNewMip,
	openQuickCreate,
	onIPAccess,
	dealId,
	mips,
	isRefreshingPermissions,
}: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { value: eventLogEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.DEAL_EVENT_LOG);

	const mipCount = mips?.length || 0;

	const links: LinkTile[] = [
		{
			title: tAdminDashboard('deal_details.links.cap_table'),
			link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_TABLE}`, {
				dealId,
			}),
			icon: 'table-v3' as PredefinedIcons,
			imageBg: '/context/admin/dashboard/cap-table.svg',
			dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_CAP_TABLE_TILE,
		},
		{
			title: tAdminDashboard('deal_details.links.cap_chart'),
			link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_CHART}`, {
				dealId,
			}),
			icon: 'branch' as PredefinedIcons,
			imageBg: '/context/admin/dashboard/cap-chart.svg',
			dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_CAP_CHART_TILE,
		},
		{
			title: tAdminDashboard('deal_details.links.deal_directory'),
			link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}`, {
				dealId,
				personId: '',
			}),
			icon: 'open-book',
			imageBg: '/context/admin/dashboard/deal-directory.svg',
			dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_DIRECTORY_TILE,
		},
		{
			title: tAdminDashboard('deal_details.links.document_library'),
			link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.DOCUMENT_LIBRARY}`, {
				dealId,
			}),
			icon: 'document-v3' as PredefinedIcons,
			imageBg: '/context/admin/dashboard/document-library.svg',
			dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_DOCUMENT_LIBRARY_TILE,
		},
		{
			title: tAdminDashboard('deal_details.links.valuation'),
			link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.WATERFALL}`, { dealId }),
			icon: 'bar-chart' as PredefinedIcons,
			imageBg: '/context/admin/dashboard/valuations.svg',
			dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_VALUATION_TILE,
		},
		...(eventLogEnabled
			? [
					{
						title: tAdminDashboard('deal_details.links.event_log'),
						link: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.EVENT_LOG}`, { dealId }),
						icon: 'activity' as PredefinedIcons,
						dataTestId: TEST_ELEMENT_IDS.DEAL_DASHBOARD_EVENT_LOG_TILE,
					},
				]
			: []),
	];

	const tileWidth = (index: number) =>
		index < 2
			? { flex: '1 auto', width: 'calc(50% - 0.5rem)' }
			: { flex: '1 auto', width: 'calc(33.333% - 0.67rem)' };

	const makeTileSkeletons = (linkTiles: LinkTile[]) => (
		<Flex wrap="wrap" gap="1rem" style={{ flex: 1 }}>
			{linkTiles.map((item, index) => (
				<Flex sx={tileWidth(index)} key={item.title}>
					<Skeleton height={156} radius="md" data-testid={`${item.dataTestId}-loading`} />
				</Flex>
			))}
		</Flex>
	);

	const makeTiles = (linkTiles: LinkTile[]) => {
		return (
			<Flex wrap="wrap" gap="1rem" style={{ flex: 1 }}>
				{linkTiles.map((item, index) => {
					return (
						<Flex sx={tileWidth(index)} key={index}>
							<DealLinkTile
								title={item.title}
								link={item.link}
								icon={item.icon}
								dataTestId={item.dataTestId}
								background={
									item.imageBg ? (
										<AssetsLoader
											src={item.imageBg}
											style={{
												position: 'absolute',
												height: '100%',
												width: 'auto',
												top: 0,
												right: 0,
											}}
										/>
									) : undefined
								}
							/>
						</Flex>
					);
				})}
			</Flex>
		);
	};

	// Two ways to create a programme — outright from the drawer, or configured in the wizard.
	const makeCreateMIPAction = (
		label: string,
		testId: string,
		buttonVariant: ButtonProps['variant'] = 'subtle'
	) => (
		<PermissionWrapper
			resourceType={ResourceType.DEAL}
			resourceId={defaultToEmptyString(dealId)}
			permissionRole={PermissionRole.ADMIN}
		>
			<Menu width={280} shadow="md" position="bottom-start" withinPortal>
				<Menu.Target>
					<Button
						variant={buttonVariant}
						leftSection={<Icon variant="add" size="lg" />}
						rightSection={<Icon variant="caret-down" size="lg" />}
						disabled={createMipLoading}
						data-testid={testId}
					>
						{label}
					</Button>
				</Menu.Target>

				<StyledCreateMipDropdown>
					<Flex direction="column" gap="0.25rem">
						<StyledCreateMipItem
							onClick={openQuickCreate}
							data-testid={
								TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_QUICK_CREATE_MIP_OPTION
							}
						>
							<Text variant="p-sm">
								{tAdminDashboard('deal_details.ip_section.create_option')}
							</Text>
						</StyledCreateMipItem>
						<StyledCreateMipItem
							onClick={createNewMip}
							data-testid={
								TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_CONFIGURE_MIP_OPTION
							}
						>
							<Text variant="p-sm">
								{tAdminDashboard('deal_details.ip_section.configure_option')}
							</Text>
						</StyledCreateMipItem>
					</Flex>
				</StyledCreateMipDropdown>
			</Menu>
		</PermissionWrapper>
	);

	return (
		<Flex direction="column" w="100%" gap="1rem">
			{isRefreshingPermissions ? (
				<Flex gap="1rem">{makeTileSkeletons(links)}</Flex>
			) : (
				<PermissionWrapper
					resourceType={ResourceType.DEAL}
					resourceId={defaultToEmptyString(dealId)}
					permissionRole={PermissionRole.VIEWER}
				>
					<Flex gap="1rem">{makeTiles(links)}</Flex>
				</PermissionWrapper>
			)}
			<MipSectionWrapper $mipCount={mipCount}>
				<ContainerWithHeader
					title={tAdminDashboard('deal_details.ip_section.title')}
					icon={<Icon variant="plant-grown" size="xl" />}
					headerActions={makeCreateMIPAction(
						tAdminDashboard('deal_details.ip_section.new_btn_label'),
						TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_MODAL_ADD_NEW_MIP_BUTTON
					)}
					style={{ overflow: 'hidden' }}
				>
					{!mips?.length && (
						<Flex>
							<EmptyState
								icon="es-rocket"
								iconSize="md"
								title={tAdminDashboard('deal_details.ip_section.empty.message')}
							>
								{makeCreateMIPAction(
									tAdminDashboard(
										'deal_details.ip_section.empty.create_btn_label'
									),
									TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_MODAL_ADD_NEW_INVESTMENT_PROGRAMME_BUTTON,
									'default'
								)}
							</EmptyState>
						</Flex>
					)}

					<Grid gutter="sm">
						{mips?.map((mip) => (
							<Grid.Col span={{ md: 4 }} key={mip.id}>
								<DealMipCard onClick={() => onIPAccess(mip)} ip={mip} />
							</Grid.Col>
						))}
					</Grid>
				</ContainerWithHeader>
			</MipSectionWrapper>
		</Flex>
	);
};
